import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ItemBuilder from './ItemBuilder';
import GiftShop from './GiftShop';

// The url key for the open product, mirroring GiftShop's `category`.
const PRODUCT_PARAM = 'product';
import Button from '../../../Button/v2';
import { useEmailInterceptor } from '../../../EmailInterceptor/EmailInterceptor';
import { usePrevious } from '../../../PackageBuilders/MultiPose/hooks';
import giftAddonVisible from './giftAddonVisible';
import productKeyOf from './productKey';
import eligiblePoses from '../../../PackageBuilders/MultiPose/poseEligibility';
import useImageOrientations from '../../../../hooks/useImageOrientations';
import gradAddonEligible from '../../../../utils/gradAddonEligibility';
import ItemSubtitle, { isAnnouncement } from './ItemSubtitle';

// Re-exported from their own file now: GiftShop and ItemBuilder both draw the
// price line, and ItemAdder imports GiftShop, so defining it here made the two
// modules import each other. Kept as named exports because existing callers and
// tests reach for them at this path.
export { ItemSubtitle, isAnnouncement };

export default function ItemAdder({
  mutation, addons, backgrounds, options, poses = [], siblingImages = [], onSelectBackground, offer = null, qualifiesForFreeAnnouncements = false, hasFreeMagnets = false, pricelistSeason = null, orderPackage = null, inOrderFlow = false, orderType = null, addedAddons = new Map(), onOpenAdded = () => {}, onProductScreenChange = () => {},
}) {
  const [addonSheetsPerType, setAddonSheetsPerType] = useState({});
  const [pose, setPose] = useState(null);
  const [background, setBackground] = useState(null);
  const { intercept } = useEmailInterceptor();


  // Filter addons before rendering. Senior-ness decided ONCE (pricelist season
  // / any senior pose — see gradAddonEligible for why NOT poses[2], whose
  // index lottery hid announcements).
  const seniorCustomer = gradAddonEligible({ pricelistSeason, poses });

  // WHICH PHOTOS A GIVEN PRODUCT DRAWS FROM -- one answer, asked by both the
  // shelf filter below and the product screen's thumbnails. They used to pick
  // separately (the screen branched on `sibling`), and two sources is how a
  // tile can say "usable" while its screen offers nothing. A sibling add-on
  // never reaches this shelf (giftAddonVisible excludes `sibling`), but the
  // helper keeps the branch for the screen and makes the two provably equal.
  const posesFor = (addon) => (
    addon?.cssName === 'sibling' ? siblingImages : (poses.length > 0 ? poses : siblingImages)
  );

  // Every photo this customer could put on anything, measured ONCE up front.
  // poseEligibility needs real dimensions to refuse a landscape pose --
  // useImageOrientations prefetches into a shared cache, keyed on the urls, so
  // this is one probe per image for the whole page, not one per surface.
  const posePreviewUrls = useMemo(
    () => [...poses, ...siblingImages].map((p) => p.watermarkUrl || p.imageUrl).filter(Boolean),
    [poses, siblingImages],
  );
  const poseOrientations = useImageOrientations(posePreviewUrls);

  // ⚠️ A PRODUCT A STUDENT HAS NO PHOTO FOR IS NOT OFFERED. The weekly magnet
  // stands the student on the artwork as an upright cut-out; an all-JPG session
  // (no transparent cut-outs at all -- common for elementary) has nothing it
  // can hold. Offering the tile anyway walks the family into a product screen
  // with no photo to choose, and in production (2026-10-15) a fallback then
  // put all four of her JPGs back on offer. The free-magnet offer already
  // hides itself for exactly this session (freeMagnetsVisibility); this is the
  // same decision for the paid product.
  //
  // An empty source means the poses have not ARRIVED (usePoses serves
  // placeholderData [] while fetching) -- judging on that would hide every
  // cut-out product for every customer for the first paint. Not known yet is
  // not the same as none. Unmeasured orientation is likewise kept, so a
  // landscape-only session is hidden once its images have been probed.
  //
  // ⚠️ THIS FILTER ALSO RESOLVES THE URL. `selectedAddonType` below matches
  // `?product=` against filteredAddons, so a refresh on a product hidden here
  // lands on the SHELF -- the documented contract for any product this
  // customer cannot see -- not on a missing product. Tested.
  const hasUsablePose = (addon) => {
    const source = posesFor(addon);

    return source.length === 0 || eligiblePoses(source, addon, poseOrientations).length > 0;
  };

  const filteredAddons = addons.filter((addon) => (
    giftAddonVisible(addon, { seniorCustomer, hasFreeMagnets }) && hasUsablePose(addon)
  ));

  // ⚠️ WHICH PRODUCT IS OPEN LIVES IN THE URL, not in component state, so the
  // browser's Back button works. It did not: opening a product changed nothing
  // about the url and added no history entry, so Back left the storefront
  // altogether and dropped the customer out of the add-ons step -- measured,
  // `history.length` stayed at 2 and Back landed on the page before the shelf.
  //
  // Derived rather than mirrored, exactly as GiftShop does with `category`:
  // the browser changes the url, this recomputes, and there is no second copy
  // to fall out of step. It also makes a product screen linkable.
  //
  // A `product` naming something this customer cannot see -- a shared link, an
  // old bookmark, or an add-on `giftAddonVisible` has since dropped -- resolves
  // to the shelf rather than to an empty product screen.
  const history = useHistory();
  const location = useLocation();
  //
  // ⚠️ `productKey`, NOT `cssName`. css_name is the RENDERER -- which case in
  // Item.jsx draws the pose, and where on the artwork it sits. Two rows can
  // legitimately share one and still be different products: the neutral and
  // school-theme weekly dry-erase magnets are both `weeklycutout` because the
  // photo lands in the same place on both boards. Grouping the screen by
  // css_name put them on ONE screen under the first one's name and thumbnail.
  // `productKey` defaults to css_name server-side, so the quantity-tier
  // families that are MEANT to share a screen (the five Wallets rows, one
  // artwork, one stepper each) are untouched.
  const productParam = new URLSearchParams(location.search).get(PRODUCT_PARAM) || '';
  const selectedAddonType = filteredAddons.some((addon) => productKeyOf(addon) === productParam)
    ? productParam
    : '';

  // Push, not replace: opening a product is a place the customer can come Back
  // from. Every other param is carried across -- sch, sid, access_code, pid and
  // optional all have to survive, and dropping one would reset the session.
  const setSelectedAddonType = useCallback((next) => {
    const params = new URLSearchParams(location.search);

    if (next) params.set(PRODUCT_PARAM, next);
    else params.delete(PRODUCT_PARAM);

    history.push(`${location.pathname}?${params.toString()}`);
  }, [history, location.pathname, location.search]);

  // Below the derivation above, which it reads. Unconditional either way, so
  // the hook order is the same on every render.
  const previousAddonType = usePrevious(selectedAddonType);

  // The page-level CheckoutBar has to know a product screen is up, because both
  // it and StickyBuyBar are `fixed bottom-0` and they overlap on a phone.
  // ItemAdder is the only thing that knows which of the two screens is showing.
  useEffect(() => {
    onProductScreenChange(selectedAddonType !== '');
  }, [selectedAddonType]);

  const handleConfirmation = (addonSheets) => intercept(() => {
    setAddonSheetsPerType((prev) => ({ ...prev, [selectedAddonType]: addonSheets }));
    // setSelectedAddonType('');
    mutation.mutate({ addonSheets });
  });

  useEffect(() => {
    if (selectedAddonType === previousAddonType) return;

    setPose(null);
    setBackground(null);
  }, [selectedAddonType]);

  // The poses a customer may see on this product, and the first of them as the
  // opening preview.
  //
  // ⚠️ THIS USED TO RE-RANDOMISE EVERY 2 SECONDS. A setInterval swapped the pose
  // or the background of the product screen's preview on a timer, so the image a
  // customer was looking at changed while they read the description and decided
  // on a quantity — and which pose they ended up buying was whichever the timer
  // had landed on when they pressed Continue. The product screen now shows the
  // poses as thumbnails and lets them choose, which is both calmer and the thing
  // they were trying to do.
  // The add-on whose screen is open. Every row sharing a product key is the
  // same design at different quantities, so the first answers for all of them.
  const openAddon = filteredAddons.find((addon) => productKeyOf(addon) === selectedAddonType);

  // SAME source the shelf judged this product by -- see posesFor.
  const offeredPoses = posesFor(openAddon);

  // WHICH OF THOSE THIS PRODUCT CAN ACTUALLY HOLD.
  //
  // ⚠️ THE SAME RULE THE MULTIPOSE BUILDER APPLIES, from one module. Both
  // screens let a customer pick the pose that goes on an add-on, so a pose
  // refused here and offered there is a pose that reaches the product anyway --
  // and `weeklycutout` stands the student on the artwork with no frame, where a
  // landscape pose overflows and a JPG lands as a rectangle of studio canvas.
  //
  // There is no Order template to derive from on this screen, so the JPG
  // fallback is `false` (allowed) -- which is exactly what this screen did
  // before, leaving the placement's rule as the only thing that changes it.
  // (Measurement happened once, above, for the shelf filter.)

  const selectablePoses = eligiblePoses(offeredPoses, openAddon, poseOrientations);

  useEffect(() => {
    if (selectedAddonType === '') return;

    setBackground(backgrounds[0]);
  }, [selectedAddonType]);

  // ⚠️ THE SELECTION IS RE-VALIDATED, NOT JUST THE THUMBNAILS, and it has to be.
  //
  // `useImageOrientations` starts at `{}` and fills in an EFFECT -- even on a
  // warm cache -- so the render that OPENS a product screen has measured
  // nothing. `eligiblePoses` deliberately keeps unmeasured poses (so the
  // carousel cannot empty and refill), which means `selectablePoses[0]` on that
  // first render can legitimately be a pose this product refuses. One commit
  // later the measurements land, the thumbnail row drops it -- and `pose` state
  // still holds it. ItemBuilder builds the addon sheet straight from `pose`, so
  // a customer opening a linkable product url (`?product=weeklycutout-2`) would
  // see only the portrait offered and still buy the landscape one by pressing
  // Continue. Filtering the list without re-checking the choice fixes nothing.
  //
  // Keyed on the ELIGIBLE IDS rather than the array, which is rebuilt every
  // render; the membership guard then makes this a no-op once the choice is
  // legal, so a customer's own pick is never overridden.
  const selectableIds = selectablePoses.map((p) => p.id).join(',');

  useEffect(() => {
    if (selectedAddonType === '') return;
    if (selectablePoses.length === 0) return;
    if (pose && selectablePoses.some((p) => p.id === pose.id)) return;

    setPose(selectablePoses[0]);
  }, [selectedAddonType, selectableIds]);

  if (selectedAddonType !== '') {
    if (!pose) return null;
    if (!background) return null;

    const isSiblingPose = pose.id === siblingImages[0]?.id;

    const builder = (
      <ItemBuilder
        isLoading={mutation.isLoading}
        isSiblingPose={isSiblingPose}
        selectedAddonType={selectedAddonType}
        // filteredAddons, NOT the raw list: senior/elementary variants share a
        // product key, so filtering the raw prop here would show both audiences'
        // rows (at both prices) on the detail screen.
        addons={filteredAddons.filter((addon) => productKeyOf(addon) === selectedAddonType)}
        // The whole shelf, for the "You might also like" row on that screen.
        visibleAddons={filteredAddons}
        onSelectProduct={setSelectedAddonType}
        initialItems={addonSheetsPerType[selectedAddonType] || []}
        background={background}
        backgrounds={backgrounds}
        orderPackage={orderPackage}
        pose={pose}
        poses={selectablePoses}
        onSelectPose={setPose}
        offer={offer}
        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
        onConfirm={handleConfirmation}
        onCancelProps={{ onClick: () => setSelectedAddonType(''), text: 'Back' }}
      />
    );
    /* The "Continue to Cart" bar used to sit here. Its replacement is
       CheckoutBar, which HolidayBuilder renders at page level -- fixed to the
       viewport, so it does not need to be inside whichever view is showing.
       `showSkipToCart` / `onSkipToCart` went with it rather than being carried
       as dead parameters. */
    return builder;
  }

  /* useEffect(() => {
    addons.forEach(addon => {
      console.log(addon);
      console.log('Addon imageUrl:', addon._attributes.cssName);
    });
  }, [addons]);

  useEffect(() => {
    poses.forEach(pose => {
      console.log(pose);
      console.log('Pose imageUrl:', pose._attributes.url);
    });
  }, [poses]); */

  const handleSelectBackground = () => intercept(() => {
    onSelectBackground(Object.values(addonSheetsPerType).flat());
  });

  // filteredAddons is THE addon filter — this render previously carried its own
  // inline duplicate (gated on poses[0]) that had drifted from it, so fixes to
  // one copy silently didn't reach the page. One implementation only.
  //
  // GiftShop owns the storefront (category sidebar, search, sort, view); this
  // component's remaining job is which products a customer may see and which one
  // they picked.
  return (
    <>
      <GiftShop
        addons={filteredAddons}
        // The shelf TILE composites a pose onto the product too, so it has to
        // ask the same eligibility question the product screen does -- else a
        // customer meets the broken composite before they ever click. Shared
        // measurement: these are the same urls ItemAdder already prefetched.
        poseOrientations={poseOrientations}
        addedAddons={addedAddons}
        inOrderFlow={inOrderFlow}
        onOpenAdded={onOpenAdded}
        orderType={orderType}
        onSelect={setSelectedAddonType}
        poses={poses}
        backgrounds={backgrounds}
        orderPackage={orderPackage}
        offer={offer}
        qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
      />

      {false && (
        <div>
          <Button onClick={handleSelectBackground}>Select Backgrounds</Button>
        </div>
      )}

    </>
  );
}
